// Shared with MYKYC_API/myKyc_adverse_media_article_policy.cjs. Keep byte-identical.
// Stage 1: article policy only. Bookmarks and subject aggregation are not inputs.
const ARTICLE_POLICY_VERSION = 'adverse-article-v1';
const ARTICLE_CONDUCT_VERSION = 'adverse-conduct-v1';
const ENHANCED_REVIEW_VERSION = 'adverse-official-finding-v1';
const ORG_CONCERN_POLICY_VERSION = 'adverse-org-concern-v1';
const SEVERITY_SCORES = { none: 0, low: 20, medium: 55, high: 85, unclear: null };
const CASE_STATUS_LABELS = {
  allegation: 'Allegation', investigation: 'Investigation', charged: 'Charged',
  conviction: 'Conviction', regulatory_finding: 'Regulatory finding',
  settlement: 'Settlement', acquitted: 'Acquitted', dismissed: 'Dismissed',
};
const CONCERN_LABELS = {
  high: 'High concern', medium: 'Medium concern', low: 'Low concern',
  review: 'Review needed', unavailable: 'Assessment unavailable', none: '',
};

function articleFindingConcern({ involvement, severity, caseStatus, nameStrength, sourceQuality, capAllegationsAtMedium = false }) {
  // Do not mistake unknown involvement for an established contextual mention.
  if (involvement === 'contextual') return 'low';
  if (involvement !== 'direct') return 'review';
  if (caseStatus === 'acquitted' || caseStatus === 'dismissed') return 'review';
  if (!['strong', 'moderate'].includes(nameStrength)) return 'review';
  if (!['high', 'medium'].includes(sourceQuality)) return 'review';
  if (severity === 'high') {
    if (caseStatus === 'allegation' && capAllegationsAtMedium === true) return 'medium';
    return nameStrength === 'strong' && sourceQuality === 'high' ? 'high' : 'medium';
  }
  if (severity === 'medium') return 'medium';
  if (severity === 'low') return 'low';
  // Direct implication with no intelligible conduct is not a clean assessment.
  return 'review';
}

function allegationCapEnabled(concernPolicy) {
  return concernPolicy?.version === ORG_CONCERN_POLICY_VERSION && concernPolicy.capAllegationsAtMedium === true;
}

// Called by the server with the authorised organisation setting, never a browser flag.
// Persist the policy with the assessment so reopening it does not change its rating.
function applyOrgConcernPolicy(assessment, { capAllegationsAtMedium = false } = {}) {
  const result = { ...assessment, concernPolicy: {
    version: ORG_CONCERN_POLICY_VERSION, capAllegationsAtMedium: capAllegationsAtMedium === true,
  } };
  result.articleConcern = assessArticleConcern(result);
  return result;
}

// Only the server's explicit, evidence-validated no-conduct result can bypass
// identity review. Empty parties/findings or reassuring summary prose are not proof.
function hasNoAdverseConduct(assessment) {
  return assessment?.assessmentVersion === ARTICLE_POLICY_VERSION
    && assessment.articleUsable === true
    && assessment.conductAssessment?.version === ARTICLE_CONDUCT_VERSION
    && assessment.conductAssessment.outcome === 'none'
    && typeof assessment.conductAssessment.evidence === 'string'
    && assessment.conductAssessment.evidence.trim().length >= 8
    && assessment.evidenceStatus === 'assessed'
    && assessment.amlSeverityLabel === 'none'
    && assessment.involvement !== 'direct'
    && Array.isArray(assessment.findings) && assessment.findings.length === 0
    && Array.isArray(assessment.accusedParties) && assessment.accusedParties.length === 0;
}

function assessArticleConcern(assessment) {
  const { nameAssessment, involvement, findings = [], sourceQuality, articleUsable = true, enhancedReview, concernPolicy } = assessment;
  if (!articleUsable) return 'unavailable';
  if (hasNoAdverseConduct(assessment)) return 'low';
  if (!nameAssessment || !Number.isFinite(nameAssessment.score)) return 'review';
  if (nameAssessment.score === 0) return 'none';
  if (involvement === 'contextual') return 'low';
  if (!findings.length) return 'review';
  const levels = findings.map((f, findingIndex) => {
    const original = articleFindingConcern({ ...f, nameStrength: nameAssessment.strength, sourceQuality,
      capAllegationsAtMedium: allegationCapEnabled(concernPolicy) });
    // Only server-verified, evidence-bound adjustments affect the same finding.
    // Neither severity nor the original finding is overwritten.
    const adjusted = enhancedReview?.version === ENHANCED_REVIEW_VERSION && enhancedReview.status === 'completed'
      && enhancedReview.originalConcern === 'high' && (enhancedReview.adjustments || []).some(a =>
        a.findingIndex === findingIndex && a.findingEvidence === f.evidence && a.findingSummary === f.summary
        && a.concern === 'medium' && a.authority && a.supportingQuote && a.scopeQuote);
    return original === 'high' && adjusted ? 'medium' : original;
  });
  // An outcome for one incident never clears a separate serious active incident.
  return ['high', 'medium', 'review', 'low'].find(level => levels.includes(level)) || 'review';
}

function sourceQualityFromScore(score) {
  if (score == null || !Number.isFinite(Number(score)) || Number(score) === 55) return 'unknown';
  return Number(score) >= 75 ? 'high' : Number(score) >= 55 ? 'medium' : 'low';
}

function articlePresentation(row, result) {
  const a = result?.assessment;
  const sourceQuality = a?.sourceQuality || row?.sourceQualityLevel || sourceQualityFromScore(row?.sourceQualityScore);
  const current = a?.assessmentVersion === ARTICLE_POLICY_VERSION;
  // Historical assessments lack party-linked evidence. Never invent v1 findings.
  const concern = !result || result.ok !== true ? 'unavailable' : !current ? 'review' : assessArticleConcern(a);
  return {
    concern, concernLabel: CONCERN_LABELS[concern], sourceQuality,
    nameStrength: current ? a?.nameAssessment?.strength : null,
    implicated: current && a?.involvement === 'direct',
    statuses: current ? [...new Set((a?.findings || []).map(f => f.caseStatus))]
      .filter(status => CASE_STATUS_LABELS[status]).map(status => CASE_STATUS_LABELS[status]) : [],
  };
}

function articleSelectionLabel(count) {
  return !count ? 'No articles selected' : `${count} article${count === 1 ? '' : 's'} selected`;
}

function reconcileArticleSelections(previousRows, selectedIds, nextRows) {
  const selected = new Set(selectedIds || []);
  const identity = row => JSON.stringify([row.subjectName, row.link]);
  const bookmarks = new Set((previousRows || []).filter(row => selected.has(String(row.id))).map(identity));
  return (nextRows || []).filter(row => selected.has(String(row.id)) || bookmarks.has(identity(row))).map(row => String(row.id));
}

module.exports = {
  ARTICLE_POLICY_VERSION, ARTICLE_CONDUCT_VERSION, ENHANCED_REVIEW_VERSION, ORG_CONCERN_POLICY_VERSION, SEVERITY_SCORES, CASE_STATUS_LABELS, CONCERN_LABELS,
  allegationCapEnabled, applyOrgConcernPolicy,
  hasNoAdverseConduct, articleFindingConcern, assessArticleConcern, articlePresentation,
  sourceQualityFromScore, articleSelectionLabel, reconcileArticleSelections,
};
